// FE-SCRM-057 — React Query hooks for proposals.
//
// Mutations invalidate two caches:
//
//   [PROPOSALS_QUERY_KEY, opportunity_id]   — proposal list refresh.
//   [ACTIVITIES_QUERY_KEY, opportunity_id]  — mark-sent appends a
//   ``proposal_delivered`` SalesActivity on the BE; invalidating
//   forces the OpportunityDetailPage activity rail to pick it up.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ACTIVITIES_QUERY_KEY } from "../OpportunityDetail/hooks";

import {
  fetchProposalDownload,
  fetchProposalsList,
  markProposalResponse,
  markProposalSent,
  uploadProposal,
} from "./api";
import type {
  DownloadProposalResponse,
  MarkResponseInput,
  MarkSentInput,
  Proposal,
  ProposalListResponse,
} from "./types";


export const PROPOSALS_QUERY_KEY = "opportunity-proposals" as const;


export function useProposalsListQuery(
  opportunity_id: string | undefined,
): UseQueryResult<ProposalListResponse> {
  return useQuery({
    queryKey: [PROPOSALS_QUERY_KEY, opportunity_id],
    queryFn: ({ signal }) =>
      fetchProposalsList(opportunity_id!, { signal }),
    enabled: !!opportunity_id,
  });
}


export function useUploadProposalMutation(
  opportunity_id: string,
): UseMutationResult<
  Proposal, unknown, { file: File; notes?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => uploadProposal(opportunity_id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROPOSALS_QUERY_KEY, opportunity_id],
      });
    },
  });
}


export function useMarkProposalSentMutation(
  opportunity_id: string,
): UseMutationResult<
  Proposal, unknown, { proposal_id: string; input: MarkSentInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proposal_id, input }) =>
      markProposalSent(proposal_id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROPOSALS_QUERY_KEY, opportunity_id],
      });
      // Mark-sent appends a ``proposal_delivered`` SalesActivity on
      // the BE (which BE-SCRM-023's auto-Touchpoint rule then turns
      // into a contact event). Invalidate so the rail surfaces it.
      qc.invalidateQueries({
        queryKey: [ACTIVITIES_QUERY_KEY, opportunity_id],
      });
    },
  });
}


export function useMarkProposalResponseMutation(
  opportunity_id: string,
): UseMutationResult<
  Proposal, unknown, { proposal_id: string; input: MarkResponseInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proposal_id, input }) =>
      markProposalResponse(proposal_id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [PROPOSALS_QUERY_KEY, opportunity_id],
      });
    },
  });
}


export function useProposalDownloadMutation(): UseMutationResult<
  DownloadProposalResponse, unknown, string
> {
  return useMutation({
    mutationFn: (proposal_id: string) =>
      fetchProposalDownload(proposal_id),
  });
}
